import { styled } from '@mui/material';
import moment from 'moment';
import { Button, Container, Divider, Icon, Paper, RubricTable, Stack, Tag, Text } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';

const Tablet = ({ assignment, batch, feedback, onClickBack, onClickGetFeedback, onClickViewFeedback }) => {
    return (
        <Container device={'tablet'}>
            <CustomStack spacing={5}>
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    Back
                </Button>
                <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                    {assignment.title}
                </Text>
                <CustomPaper elevation={0}>
                    <Stack divider={<Divider />} spacing={4}>
                        <Stack alignItems={'flex-start'} spacing={4}>
                            <Stack spacing={2}>
                                <Tag type={'attempts'} value={FEEDBACK_LIMIT - (feedback.length + batch.length)} />
                                <Text component={'p'} variant={'h6'} weight={'medium'}>
                                    Assignment Feedback
                                </Text>
                                <Stack spacing={5}>
                                    <Text>
                                        TimelyGrader AI utilizes the assignment rubric and examples of your instructor's
                                        feedback to deliver personalized feedback tailored to this deliverable. This
                                        gives you a chance to get feedback and improve your work before you submit!
                                    </Text>
                                    <Text>
                                        Important: Submitting drafts is not considered your final submission. Please
                                        submit your final work as per your instructor's instructions.
                                    </Text>
                                </Stack>
                            </Stack>
                            <Button
                                disabled={feedback.length + batch.length === FEEDBACK_LIMIT}
                                onClick={onClickGetFeedback}
                            >
                                Upload a draft ({FEEDBACK_LIMIT - (feedback.length + batch.length)}/{FEEDBACK_LIMIT})
                            </Button>
                        </Stack>
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>Your Feedback</Text>
                                <Tag type={'general'} value={'status'} />
                            </Stack>
                            {!feedback.length && !batch.length ? (
                                <Text color={'light'}>
                                    Nothing to show yet! Get started by uploading an assignment to get feedback.
                                </Text>
                            ) : (
                                <Stack spacing={3}>
                                    {feedback.map((feedbackItem, index) => (
                                        <Stack
                                            key={feedbackItem.id}
                                            sx={{ minWidth: 0 }}
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                            spacing={2}
                                        >
                                            <Stack>
                                                <Text color={'light'} variant={'p3'}>
                                                    {moment(feedbackItem.created_on).format('MMM D [@] hh:mma')}
                                                </Text>
                                                <Text>
                                                    {typeof feedbackItem.feedback === 'string'
                                                        ? `Attempt ${index + 1}`
                                                        : `${feedbackItem.submission.name}`}
                                                </Text>
                                            </Stack>
                                            <Button
                                                variant={'borderless'}
                                                onClick={() => onClickViewFeedback(feedbackItem)}
                                            >
                                                View
                                            </Button>
                                        </Stack>
                                    ))}
                                    {batch.map((batchItem) => (
                                        <Stack
                                            key={batchItem.id}
                                            sx={{ minWidth: 0 }}
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                            spacing={2}
                                        >
                                            <Stack>
                                                <Text color={'light'} variant={'p3'}>
                                                    {moment(batchItem.created_on).format('MMM D [@] hh:mma')}
                                                </Text>
                                                <Text>{batchItem.files[0].data.name}</Text>
                                            </Stack>
                                            <Text sx={{ flexShrink: 0 }} color={'light'}>
                                                Processing...
                                            </Text>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </CustomPaper>
                <CustomPaper elevation={0}>
                    <Stack spacing={5}>
                        <Stack spacing={2}>
                            <Text>Assignment Description</Text>
                            {assignment.description ? (
                                <Text color={'light'} rich>
                                    {assignment.description}
                                </Text>
                            ) : (
                                <Text color={'light'}>None</Text>
                            )}
                        </Stack>
                        <Stack spacing={2}>
                            <Text>Assignment Rubric</Text>
                            {assignment.rubric ? (
                                <RubricTable isExpanded type={'view'} data={assignment.rubric.criteria} />
                            ) : (
                                <Text color={'light'}>None</Text>
                            )}
                        </Stack>
                    </Stack>
                </CustomPaper>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(5),
    };
});

export default Tablet;
